import { isPlatformBrowser } from '@angular/common';
import type { OnChanges } from '@angular/core';
import { Directive, ElementRef, PLATFORM_ID, Renderer2, inject, input } from '@angular/core';

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[skeletonInline]',
})
export class SkeletonInlineDirective implements OnChanges {
  private host = inject<ElementRef<HTMLElement>>(ElementRef);
  private r = inject(Renderer2);

  readonly showSkeleton = input(false, { alias: 'skeletonInline' });
  readonly skeletonWidth = input<string>();
  readonly skeletonHeight = input<string>();
  readonly skeletonBorderRadius = input('var(--border-radius-sm, 8px)');
  readonly skeletonThemeClass = input<string>();

  private isApplied = false;
  private originalStyles: Record<string, string> = {};

  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private static readonly MANAGED_STYLE_PROPERTIES = ['display', 'color', 'width', 'height', 'border-radius', 'pointer-events', 'user-select'] as const;

  ngOnChanges(): void {
    if (!this.isBrowser) {
      return;
    }

    if (this.showSkeleton()) {
      this.applySkeleton();
    } else {
      this.restoreOriginalStyles();
    }
  }

  private applySkeleton(): void {
    if (this.isApplied) {
      return;
    }

    const el = this.host.nativeElement;
    const style = el.style;

    for (const p of SkeletonInlineDirective.MANAGED_STYLE_PROPERTIES) {
      this.originalStyles[p] = style.getPropertyValue(p);
    }

    const width = this.skeletonWidth() || this.measureWidth(el) || '120px';
    const height = this.skeletonHeight() || '1em';

    this.r.addClass(el, 'sk');
    const skeletonThemeClass = this.skeletonThemeClass();

    if (skeletonThemeClass) {
      this.r.addClass(el, skeletonThemeClass);
    }

    style.setProperty('display', 'inline-block');
    style.setProperty('color', 'transparent');
    style.setProperty('width', width);
    style.setProperty('height', height);
    style.setProperty('border-radius', this.skeletonBorderRadius());
    style.setProperty('pointer-events', 'none');
    style.setProperty('user-select', 'none');

    this.r.setAttribute(el, 'aria-busy', 'true');
    this.isApplied = true;
  }

  private restoreOriginalStyles(): void {
    if (!this.isApplied) {
      return;
    }

    const el = this.host.nativeElement;
    const style = el.style;

    for (const property of SkeletonInlineDirective.MANAGED_STYLE_PROPERTIES) {
      this.restoreProperty(style, property);
      delete this.originalStyles[property];
    }

    this.r.removeClass(el, 'sk');
    const skeletonThemeClass = this.skeletonThemeClass();

    if (skeletonThemeClass) {
      this.r.removeClass(el, skeletonThemeClass);
    }

    this.r.removeAttribute(el, 'aria-busy');
    this.isApplied = false;
  }

  private measureWidth(el: HTMLElement): string | null {
    const w = Math.round(el.getBoundingClientRect().width || el.offsetWidth);

    return w ? `${w}px` : null;
  }

  private restoreProperty(style: CSSStyleDeclaration, property: string): void {
    const previous = this.originalStyles[property];

    if (previous) {
      style.setProperty(property, previous);

      return;
    }

    style.removeProperty(property);
  }
}
